
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Bell, AlertTriangle, AlertCircle, Info, CheckCircle, ArrowRight } from "lucide-react";
import { useSmartNotifications } from "@/hooks/useSmartNotifications";
import { useNavigate } from "react-router-dom";

const SmartNotifications = () => {
  const { data: notifications, isLoading } = useSmartNotifications();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'error': return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'info': return <Info className="w-5 h-5 text-blue-500" />;
      case 'success': return <CheckCircle className="w-5 h-5 text-green-500" />;
      default: return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const getNotificationVariant = (type: string) => {
    switch (type) {
      case 'error': return 'destructive' as const;
      case 'warning': return 'default' as const;
      default: return 'default' as const;
    }
  };

  const getActionRoute = (id: string) => {
    switch (id) {
      case 'pending-clients': return '/clients';
      case 'overdue-tasks': return '/tasks';
      case 'pending-bookings': return '/tourism-packages';
      default: return '/';
    }
  };

  return (
    <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
      <CardHeader>
        <CardTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <Bell className="w-5 h-5 text-orange-600" />
          التنبيهات الذكية
          {notifications && notifications.length > 0 && (
            <Badge variant="destructive" className="text-xs">
              {notifications.length}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {notifications && notifications.length > 0 ? (
            notifications.map((notification) => (
              <Alert key={notification.id} variant={getNotificationVariant(notification.type)}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    {getNotificationIcon(notification.type)}
                    <div className="flex-1">
                      <AlertDescription className="space-y-2">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold">{notification.title}</h4>
                          <Badge variant="outline" className="text-xs">
                            {notification.count}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600">{notification.message}</p>
                      </AlertDescription>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(getActionRoute(notification.id))}
                    className="shrink-0"
                  >
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </Alert>
            ))
          ) : (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" />
              <p className="font-medium text-slate-700">ممتاز! لا توجد تنبيهات</p>
              <p className="text-sm">جميع الأمور تسير بشكل طبيعي</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SmartNotifications;
